import { useEffect, useRef } from 'react';

/**
 * useSmartPolling — single source of truth for all admin polling loops.
 *
 * Why this exists:
 *   The previous panels (KitchenPanel, BarPanel, LiveTablesPanel...) each set up
 *   their own setInterval, often with unstable useCallback deps that caused the
 *   interval to be cleared/recreated on every state update. They also kept polling
 *   while the tablet was asleep or the tab hidden, draining battery and the
 *   backend event loop.
 *
 * What it does:
 *   - Runs `fetchFn` once on mount.
 *   - Polls every `intervalMs` while the document is visible.
 *   - Pauses immediately when `document.visibilityState === "hidden"`.
 *   - Resumes (and triggers an immediate fetch) when the document becomes visible.
 *   - Always reads the latest `fetchFn` via a ref so the caller can capture
 *     fresh state without forcing the interval to be torn down each render.
 *
 * @param {Function} fetchFn  Async function to call on each tick.
 * @param {number}   intervalMs Polling cadence in ms.
 * @param {boolean}  enabled    Optional kill switch (default true).
 */
export default function useSmartPolling(fetchFn, intervalMs, enabled = true) {
  const fnRef = useRef(fetchFn);

  // Keep the latest function in a ref so we don't restart the interval on every render.
  useEffect(() => {
    fnRef.current = fetchFn;
  }, [fetchFn]);

  useEffect(() => {
    if (!enabled || !intervalMs) return undefined;

    let timerId = null;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      const fn = fnRef.current;
      if (typeof fn === 'function') {
        try {
          const ret = fn();
          if (ret && typeof ret.catch === 'function') ret.catch(() => {});
        } catch (_) { /* swallow */ }
      }
    };

    const start = () => {
      if (timerId != null) return;
      // immediate fetch on (re)start, then poll
      tick();
      timerId = setInterval(tick, intervalMs);
    };

    const stop = () => {
      if (timerId != null) {
        clearInterval(timerId);
        timerId = null;
      }
    };

    const handleVisibility = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState === 'hidden') {
        stop();
      } else {
        start();
      }
    };

    // Bootstrap based on current state
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      // wait for visibility
    } else {
      start();
    }

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibility);
    }

    return () => {
      cancelled = true;
      stop();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibility);
      }
    };
  }, [intervalMs, enabled]);
}
